import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Filter } from 'lucide-react';

interface Application {
  id: string;
  name: string;
  description: string;
  category: string;
  url: string;
  icon: string;
}

const applications: Application[] = [
  {
    id: 'recruiting',
    name: 'Recruiting Portal',
    description: 'Náborový systém pre indirect a salaried pozície. Správa kandidátov, náborových žiadostí, schvaľovacích procesov a štatistík.',
    category: 'HR',
    url: 'https://recruiting.artifex-systems.sk',
    icon: '👔',
  },
  {
    id: 'pohovory',
    name: 'Pohovory',
    description: 'Plánovanie a správa pohovorov s kandidátmi. Kalendár, poznámky z pohovorov a koordinácia náborového tímu.',
    category: 'HR',
    url: 'https://pohovory.artifex-systems.sk',
    icon: '🗣️',
  },
  {
    id: 'skillmatrix',
    name: 'Skill Matrix',
    description: 'Správa a mapovanie zručností zamestnancov. Sledovanie kompetencií, certifikátov a rozvojových potrieb.',
    category: 'HR',
    url: 'https://skillmatrix.artifex-systems.sk',
    icon: '📈',
  },
  {
    id: 'masaze',
    name: 'Firemné Masáže',
    description: 'Objednávanie a správa firemných masáží. Zamestnanci si rezervujú termíny masáží priamo v práci.',
    category: 'Wellness',
    url: 'https://masaze.artifex-systems.sk',
    icon: '💆‍♂️',
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Centrálna administrácia systémov a nastavení. Správa používateľov, oprávnení a konfigurácie aplikácií.',
    category: 'Administrácia',
    url: 'https://admin.artifex-systems.sk',
    icon: '⚙️',
  },
  {
    id: 'portal',
    name: 'Portál',
    description: 'Hlavný zamestnanecký portál s prehľadom služieb, odkazmi a firemnými informáciami na jednom mieste.',
    category: 'Administrácia',
    url: 'https://portal.artifex-systems.sk',
    icon: '🏠',
  },
  {
    id: 'koucing',
    name: 'Firemný Koučing',
    description: 'Objednávanie a správa firemných koučingových služieb. Rezervácia koučingových sedení pre zamestnancov.',
    category: 'Wellness',
    url: 'https://koucing.artifex-systems.sk',
    icon: '🎯',
  },
  {
    id: 'vyrocia',
    name: 'Manažér Výročí',
    description: 'Správa a pripomínanie výročí zamestnancov. Sledovanie dôležitých dátumov a firemných osláv.',
    category: 'HR',
    url: 'https://vyrocia.artifex-systems.sk',
    icon: '🎉',
  },
  {
    id: 'nabor',
    name: 'Nábor – Operátori',
    description: 'Náborový systém pre operátorov výroby a logistiky. Správa kandidátov a náborových procesov pre výrobné pozície.',
    category: 'HR',
    url: 'https://nabor.artifex-systems.sk',
    icon: '🏭',
  },
  {
    id: 'obrazovky',
    name: 'Správa Obrazoviek',
    description: 'Pridávanie a správa obsahu na firemné obrazovky. Tvorba a publikovanie obsahu pre digitálne displeje.',
    category: 'Komunikácia',
    url: 'https://obrazovky.artifex-systems.sk',
    icon: '📺',
  },
  {
    id: 'dotazniky',
    name: 'Dotazníky',
    description: 'Vytváranie a správa dotazníkov a prieskumov. Zber odpovedí a analýza výsledkov.',
    category: 'Komunikácia',
    url: 'https://dotazniky.artifex-systems.sk',
    icon: '📝',
  },
  {
    id: 'exit',
    name: 'Exit Interview',
    description: 'Správa exit interview procesov. Zber spätnej väzby od odchádzajúcich zamestnancov a analýza trendov.',
    category: 'HR',
    url: 'https://exit.artifex-systems.sk',
    icon: '🚪',
  },
  {
    id: 'dodavatelia',
    name: 'Hodnotenie Dodávateľov',
    description: 'Hodnotenie a správa dodávateľov. Meranie výkonnosti, kvality a spokojnosti s dodávateľmi.',
    category: 'Nákup',
    url: 'https://dodavatelia.artifex-systems.sk',
    icon: '📦',
  },
  {
    id: 'agentury',
    name: 'Agency Management',
    description: 'Správa zamestnancov agentúr. Dokumenty, štatistiky a filtrovanie podľa agentúr, pozícií a kategórií.',
    category: 'HR',
    url: 'https://agentury.artifex-systems.sk',
    icon: '🏢',
  },
  {
    id: 'ulohy',
    name: 'Task Management',
    description: 'Prehľadový dashboard pre riadenie projektov a úloh. Sledovanie termínov a tímových aktivít.',
    category: 'Projekty',
    url: 'https://ulohy.artifex-systems.sk',
    icon: '📊',
  },
  {
    id: '360',
    name: '360° Hodnotenie',
    description: 'Komplexné 360° hodnotenie zamestnancov. Spätná väzba od kolegov, nadriadených a podriadených.',
    category: 'HR',
    url: 'https://360.artifex-systems.sk',
    icon: '🔄',
  },
  {
    id: 'prekladac',
    name: 'Prekladač',
    description: 'Firemný prekladač textov a dokumentov. Rýchly preklad obsahu pre internú aj externú komunikáciu.',
    category: 'Nástroje',
    url: 'https://prekladac.artifex-systems.sk',
    icon: '🌐',
  },
];

const categories = ['Všetky', 'HR', 'Wellness', 'Administrácia', 'Komunikácia', 'Nákup', 'Projekty', 'Nástroje'];

export default function Applications() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Všetky');

  const filteredApps = useMemo(() => {
    let filtered = applications;

    if (selectedCategory !== 'Všetky') {
      filtered = filtered.filter((app) => app.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.name.toLowerCase().includes(term) ||
          app.description.toLowerCase().includes(term) ||
          app.category.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [searchTerm, selectedCategory]);

  const handleAppClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))] p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p className="text-sm font-semibold uppercase tracking-widest text-secondary mb-2">
            Artifex Systems
          </p>
          <h1 className="font-display text-4xl font-bold text-primary mb-2">
            Naše aplikácie
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Interné aplikácie a nástroje pre zamestnancov. Všetky služby bežia na doméne{' '}
            <span className="font-medium text-primary">artifex-systems.sk</span>.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8 space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Hľadať aplikácie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-white border-[hsl(var(--hairline))] text-primary placeholder:text-muted-foreground focus:ring-secondary focus:border-secondary"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-muted-foreground w-5 h-5 shrink-0" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-[hsl(var(--hairline))] rounded-lg bg-white text-primary focus:ring-2 focus:ring-secondary focus:border-transparent min-w-[160px]"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            Nájdených aplikácií: {filteredApps.length} z {applications.length}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredApps.map((app, index) => (
              <motion.div
                key={app.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25, delay: index * 0.03 }}
                whileHover={{ y: -4 }}
              >
                <Card className="h-full bg-white border-[hsl(var(--hairline))] shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-lift)] transition-all duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-3xl shrink-0" aria-hidden>
                          {app.icon}
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-lg font-semibold text-primary truncate">
                            {app.name}
                          </CardTitle>
                          <Badge
                            variant="secondary"
                            className="text-xs mt-1 bg-[hsl(var(--muted))] text-muted-foreground border-0"
                          >
                            {app.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm line-clamp-3 text-muted-foreground">
                      {app.description}
                    </CardDescription>

                    <p className="text-xs text-muted-foreground/80 truncate font-mono">
                      {app.url.replace('https://', '')}
                    </p>

                    <Button
                      onClick={() => handleAppClick(app.url)}
                      className="w-full group bg-secondary hover:bg-[hsl(var(--secondary-hover))] text-secondary-foreground transition-all duration-200"
                    >
                      Otvoriť aplikáciu
                      <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredApps.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-muted-foreground mb-4">
              <Search className="w-16 h-16 mx-auto opacity-40" />
            </div>
            <h3 className="text-lg font-medium text-primary mb-2">
              Žiadne aplikácie neboli nájdené
            </h3>
            <p className="text-muted-foreground">
              Skúste upraviť vyhľadávacie kritériá alebo zvoliť inú kategóriu
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
